import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useEffect, useRef, useState } from "react";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { BsArrowClockwise, BsInfoCircle } from "react-icons/bs";
import { MdOutlineFileDownload } from "react-icons/md";
import Select from "react-select";
import Breadcrumbs from "../components/Breadcrumbs";
import ActiveWorkOrder from "../components/charts/ActiveWorkOrder";
import CompletePm from "../components/charts/CompletePm";
import DowntimeByVehicle from "../components/charts/DowntimeByVehicle";
import FleetHealthStatus from "../components/charts/FleetHealthStatus";
import MaintenanceCostOverview from "../components/charts/MaintenanceCostOverview";
import OverduePM from "../components/charts/OverduePM";
import PmSummaryCards from "../components/charts/PmSummaryCards";
import RecentlyInstalledTires from "../components/charts/RecentlyInstalledTires";
import ScheduledUnscheduledChart from "../components/charts/ScheduledUnscheduledChar";
import TireCostByPosition from "../components/charts/TireCostByPosition";
import TireCostByVehicle from "../components/charts/TireCostByVehicle";
import TopVehiclesByCost from "../components/charts/TopVehiclesByCost";
import UpCommingPM from "../components/charts/UpCommingPM";
import WorkDoneBy from "../components/charts/WorkDoneBy";
import { apiMethod, RequestType } from "../services/ApiRequest";
import { ENDPOINTS } from "../services/EndPoint";
import { hasPermission } from "../utils/helper";
import { errorNotify } from '../utils/toast';

const DataDashboard = () => {
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? "#fff" : "black",
      cursor: "pointer",
    }),
  };

  const [scheduledUnscheduledChart, setScheduledUnscheduledChart] = useState([]);
  const [workOrderData, setWorkOrderData] = useState({
    activeWorkOrdersCount: {
      pending: 0,
      in_progress: 0,
      completed: 0
    }
  });
  const [vehicleData, setVehicleData] = useState([]);
  const [tireCostByType, setTireCostByType] = useState([]);
  const [latestTyres, setLatestTyres] = useState([]);
  const [tyreOverview, setTyreOverview] = useState({
    total_expenditure: 0,
    avg_cost_per_tire: 0,
  });
  const [missingOdoMeterCounty, setMissingOdoMeterCounty] = useState(0);
  const [healthStatus, setHealthStatus] = useState(0)
  const [installedTyres, setInstalledTyres] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState({
    value: "all",
    label: "All",
  });
  const [loading, setLoading] = useState(false);
  const dashboardRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  const [maintenanceDateCount, setMaintenanceDateCount] = useState({
    upcoming_pm: 0,
    overdue_pm: 0,
    completed_pm: 0
  });
  const [upComingPm, setUpComingPm] = useState([]);
  const [CompletePmData, setCompletePmData] = useState([]);
  const [overduePm, setOverduePm] = useState([]);
  const [topVehicles, setTopVehicles] = useState([]);
  const [maintenanceCost, setMaintenanceCost] = useState([]);

  const filterOptions = [
    { value: "all", label: "All" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  useEffect(() => {
    getServiceData();
    workOrderInformation();
    vehicleInfoData();
    maintenanceCostData();
    getTyresInfo();
  }, [selectedFilter]);

  const workOrderInformation = async () => {
    const data = {
      date_type: selectedFilter.value,
    };
    setLoading(true);

    await apiMethod({
      method: RequestType.Post,
      auth: true,
      url: `${ENDPOINTS.WorkOrderInfo}`,
      body: data,
      formData: true,
      onSuccess: (res) => {
        if (res?.data) {
          setWorkOrderData(res?.data?.activeWorkOrdersCount);

          setTimeout(() => {
            setLoading(false);
          }, 1500)
        }
      },
      onFailure: (error) => {
        setLoading(false);
      },
    });
  };

  const getTyresInfo = async () => {
    const data = {
      date_type: selectedFilter.value,
    };
    setLoading(true);

    await apiMethod({
      method: RequestType.Post,
      auth: true,
      url: `${ENDPOINTS.TyreInfoApi}`,
      body: data,
      formData: true,
      onSuccess: (res) => {
        if (res?.data) {

          setTyreOverview({
            total_expenditure: res?.data?.totalTireExpenditure,
            avg_cost_per_tire: res?.data?.averageTireCost,
          })
          setInstalledTyres(res?.data?.tiresInstalledLast90Days);
          setLatestTyres(res?.data?.recentlyInstalledTyres);
          setVehicleData(res?.data?.tireCostByVehicle);

          setTimeout(() => {
            setLoading(false);
          }, 1500)
        }
      },
      onFailure: (error) => {
        setLoading(false);
      },
    });
  };

  const getServiceData = async () => {
    const data = {
      date_type: selectedFilter.value,
    };
    setLoading(true);

    await apiMethod({
      method: RequestType.Post,
      auth: true,
      url: `${ENDPOINTS.ServicesInfo}`,
      body: data,
      formData: true,
      onSuccess: (res) => {
        if (res?.data) {

          setMaintenanceDateCount(res?.data?.maintenanceDateCount)
          setUpComingPm(res?.data?.topUpcomingMaintenance)
          setOverduePm(res?.data?.topOverdueMaintenance)
          setCompletePmData(res?.data?.completePmPaginated)
          setScheduledUnscheduledChart(res?.data?.scheduledUnscheduledChart);

          setTimeout(() => {
            setLoading(false);
          }, 1500)
        }
      },
      onFailure: (error) => {
        setLoading(false);
      },
    });
  };

  const vehicleInfoData = async () => {
    const data = {
      date_type: selectedFilter.value,
    };
    setLoading(true);

    await apiMethod({
      method: RequestType.Post,
      auth: true,
      url: `${ENDPOINTS.VehicleInfoApi}`,
      body: data,
      formData: true,
      onSuccess: (res) => {
        if (res?.data) {
          setMissingOdoMeterCounty(res?.data?.totalMissingOdoMeter);
          setTopVehicles(res?.data?.topVehicleByCost);

          setTimeout(() => {
            setLoading(false);
          }, 1500)
        }
      },
      onFailure: (error) => {
        setLoading(false);
      },
    });
  };

  const maintenanceCostData = async () => {
    const data = {
      date_type: selectedFilter.value,
    };
    setLoading(true);

    await apiMethod({
      method: RequestType.Post,
      auth: true,
      url: `${ENDPOINTS.MaintenanceCostOverview}`,
      body: data,
      formData: true,
      onSuccess: (res) => {
        if (res?.data) {
          setMaintenanceCost(res?.data);

          setTimeout(() => {
            setLoading(false);
          }, 1500)
        }
      },
      onFailure: (error) => {
        setLoading(false);
      },
    });
  };

  const exportDashboardToPDF = async () => {
    setIsExporting(true);
    try {
      const sections = document.querySelectorAll(".pdf-section");
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 10;
      const contentWidth = pdfWidth - 2 * margin;

      let yOffset = margin;
      const safetyBuffer = 5; // ✅ prevents last section clipping

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];

        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const remainingPageSpace = pdfHeight - yOffset - margin;

        // ✅ Add safety buffer here to avoid bottom cut-offs
        if (imgHeight + safetyBuffer > remainingPageSpace) {
          pdf.addPage();
          yOffset = margin;
        }

        pdf.addImage(imgData, 'PNG', margin, yOffset, imgWidth, imgHeight);
        yOffset += imgHeight + 10;
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      pdf.save(`Dashboard-Report-${timestamp}.pdf`);
    } catch (error) {
      errorNotify('Failed to export dashboard. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const hasViewPermission = hasPermission("dashboard", "view");
  const hasExportPermission = hasPermission("dashboard", "export");

  if (loading) {
    return (
      <div className="loading_spinner">
        <Spinner animation="grow" />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumbs values={['Maintenance', 'Dashboard']} />

      <div className='access_view'>
        <h2>Dashboard</h2>

        <div className="d-flex align-items-center">
          {hasViewPermission && (
            <div style={{ width: "200px", cursor: "pointer" }}>
              <Select
                options={filterOptions}
                value={selectedFilter}
                onChange={setSelectedFilter}
                placeholder="Select filter"
                isSearchable={false}
                styles={customStyles}
              />
            </div>
          )}
          {hasExportPermission && (
            <div className="d-flex justify-content-end">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip
                    style={{
                      backgroundColor: '#fff',
                      color: '#3D3F5C',
                    }}
                  >
                    {isExporting ? 'Exporting...' : 'Export Dashboard'}
                  </Tooltip>
                }
              >
                <MdOutlineFileDownload
                  onClick={!isExporting ? exportDashboardToPDF : undefined}
                  style={{
                    fontSize: "35px",
                    marginLeft: "15px",
                    cursor: isExporting ? "not-allowed" : "pointer",
                    color: isExporting ? "#ccc" : "#4014B8",
                    border: `1px solid ${isExporting ? "#ccc" : "#4014B8"}`,
                    borderRadius: "6px",
                    padding: "4px",
                    opacity: isExporting ? 0.6 : 1
                  }}
                />
              </OverlayTrigger>
            </div>
          )}

        </div>
      </div>

      {
        !hasViewPermission && hasExportPermission && (
          <div className="alert alert-warning mt-4">
            You don't have permission to view the dashboard content. You can only use the export functionality.
          </div>
        )
      }
      {
        hasViewPermission && (
          <div ref={dashboardRef} style={{ paddingLeft: "25px", paddingRight: "25px" }}>
            <div className="row mb-5 pdf-section mt-4">
              <div className="col-md-12">
                <PmSummaryCards maintenanceDateCount={maintenanceDateCount} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <UpCommingPM upComingPm={upComingPm} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <OverduePM overduePm={overduePm} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <CompletePm CompletePmData={CompletePmData} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <ActiveWorkOrder workOrderData={workOrderData} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <div className="custom-box" style={{ background: "#23243a" }}>
                  <div className="custom-heading">{missingOdoMeterCounty}</div>
                  <div className="custom-text">Missing Odometer Reading Count</div>
                </div>
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <div className="custom-box" style={{ background: "#23243a" }}>
                  <div className="custom-heading" style={{ fontSize: "45px" }}>Not Available</div>
                  <div className="custom-text">Total Vehicle Downtime (hours)</div>
                </div>
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <DowntimeByVehicle />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-6">
                <FleetHealthStatus healthStatus={healthStatus} />
              </div>
              <div className="col-md-6">
                <ScheduledUnscheduledChart scheduledUnscheduledChart={scheduledUnscheduledChart} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <MaintenanceCostOverview maintenanceCost={maintenanceCost} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <TopVehiclesByCost topVehicles={topVehicles} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-12">
                <h3 className="scheduled_unscheduled_heading" style={{ fontSize: "25px", marginBottom: "18px" }}>
                  Tyre Maintenance Overview
                </h3>
              </div>

              <div className="col-md-6">
                <div
                  className="d-flex flex-column align-items-center justify-content-center shadow-lg rounded-4 p-4 mb-4 border border-secondary"
                  style={{ background: "#23243a" }}
                >
                  <div className="fs-1 fw-bold text-warning mb-2">
                    {tyreOverview.total_expenditure || 0}
                  </div>
                  <div className="small" style={{ color: "#D1D5DB" }}>
                    Total Tyre Expenditure
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="d-flex flex-column align-items-center justify-content-center shadow-lg rounded-4 p-4 mb-4 border border-secondary"
                  style={{ background: "#23243a" }}
                >
                  <div className="fs-1 fw-bold text-warning mb-2">
                    {tyreOverview.avg_cost_per_tire || 0}
                  </div>
                  <div className="small" style={{ color: "#D1D5DB" }}>
                    Avg. Tyre Cost (per tyre)
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <TireCostByVehicle vehicleData={vehicleData} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-6">
                <RecentlyInstalledTires items={latestTyres} />
              </div>

              <div className="col-md-6">
                <TireCostByPosition tireCostByType={tireCostByType} />
              </div>
            </div>

            <div className="row mb-5 pdf-section">
              <div className="col-md-12">
                <div className="card_container tire_activity">
                  <div
                    className="d-flex align-items-center justify-content-center fw-bold fs-5 mb-2"
                    style={{ color: "#4F19E2" }}
                  >
                    <BsArrowClockwise className="me-2" />
                    Tyre Activity
                    <BsInfoCircle className="ms-2 text-info" />
                  </div>
                  <div className="text-muted mb-1">
                    Tyres Installed (Last 90 Days):
                  </div>
                  <div className="fw-bold fs-4" style={{ color: "#4F19E2" }}>
                    {installedTyres}
                  </div>
                </div>
              </div>
            </div>

            <div className='row pdf-section mb-5'>
              <div className="col-md-12 pb-5">
                <h3
                  className="scheduled_unscheduled_heading"
                  style={{ fontSize: "25px", marginBottom: "18px" }}
                >
                  General Overview
                </h3>

                <WorkDoneBy filter={selectedFilter.value} />
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default DataDashboard;
